import { __, __url, getSdpUrl } from '@99designs/i18n';
import type { ChatMessage } from '../client/types';

/**
 * The component type the service uses for a call-to-action link.
 *
 * A CTA is a decoration on an assistant text message, not a question: it produces no
 * bubble of its own and no reply from the customer. Any future code that asks "is there
 * an unanswered generative-UI question right now?" must therefore exclude this type
 * rather than treat it as a pending selector.
 */
export const CTA_LINK_COMPONENT_TYPE = 'cta-link';

export interface CtaLink {
    url: string;
    label: string;
}

/**
 * The links the assistant is allowed to offer, keyed by the sentinel the model emits.
 *
 * Fail-closed by construction: the model never emits a URL, only a key, so there is no
 * arbitrary-href path to filter. A key outside this table resolves to nothing and the
 * link is silently dropped, which makes injecting a destination through the prompt
 * structurally impossible rather than merely validated against.
 *
 * Both the URL and the label are produced lazily, because `__url`, `getSdpUrl` and `__`
 * all read locale context that does not exist at module-evaluation time.
 */
const CTA_LINKS: Record<string, { getUrl: () => string; getLabel: () => string }> = {
    LOGO_MAKER_URL: {
        getUrl: () => __url('/logomaker'),
        getLabel: () =>
            __('Try Logomaker', {
                _context: 'Call-to-action link offered by the assistant in the AI brief chat',
            }),
    },
    PRO_LOGO_DESIGN_URL: {
        getUrl: () => getSdpUrl('prologodesign'),
        getLabel: () =>
            __('Get a professional logo', {
                _context: 'Call-to-action link offered by the assistant in the AI brief chat',
            }),
    },
};

/**
 * Reads a call-to-action link off an assistant message's component payload.
 *
 * A label on the wire wins, matching the pilot. The prompt asks the model for a short
 * label tailored to what was just said ("Explore logo design", "See pro designers"), so
 * overriding it with the generic whitelist label would throw away deliberate copy. The
 * cost is that a model-authored label is untranslated; the whitelist label is the
 * translated fallback for when the model omits one.
 */
export function parseCtaLink(component: ChatMessage['component']): CtaLink | undefined {
    if (component?.type !== CTA_LINK_COMPONENT_TYPE) {
        return undefined;
    }

    const data: unknown = component.data;
    if (typeof data !== 'object' || data === null) {
        return undefined;
    }

    const urlKey = (data as Record<string, unknown>)['url_key'];
    if (typeof urlKey !== 'string') {
        return undefined;
    }

    const entry = CTA_LINKS[urlKey];
    if (entry === undefined) {
        return undefined;
    }

    const url = entry.getUrl();
    if (!url) {
        return undefined;
    }

    const wireLabel = (data as Record<string, unknown>)['label'];
    const label =
        typeof wireLabel === 'string' && wireLabel.trim() !== '' ? wireLabel : entry.getLabel();

    return { url, label };
}
